import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';

import {
    DEFAULT_ADVISOR_ALIAS,
    FREE_TRIAL_ENABLED,
    STORAGE_KEY_ACCESS_TOKEN,
    STORAGE_KEY_ANON_MESSAGES_EXP,
    STORAGE_KEY_CHATS_MESSAGES_COUNT,
    STORAGE_KEY_CHATS_MESSAGES_COUNT_LEGACY,
    STORAGE_KEY_DAILY_TAROT,
    STORAGE_KEY_MEMORY_ANNOUNCEMENT_SEEN,
    STORAGE_KEY_SAVED_USER_MESSAGES,
    STORAGE_KEY_WIDGET_HOST,
    STORE_AUTH_PAYMENT_POPUP
} from '../../const';
import AuthApi from '../../service/Api/authApi';
import storageService from '../../service/StorageService';
import WSService from '../../service/WSService';
import webSocketConnector from '../../service/WSService/WebSocketConnector';
import { gaEvents } from '../../utils/analytics';
import { getAppBasename } from '../../utils/helper';

export const FilledDataType = { COMPLETE: 'complete', INCOMPLETE: 'incomplete' };

const setPaymentPopupState = (plan, limits) => {
    if (!plan || !limits) {
        return STORE_AUTH_PAYMENT_POPUP.product;
    }

    const { isFree, isTrial, isCancel } = plan;
    const { total, used } = limits;

    // With the trial switched off there is only one thing to offer a customer
    // without a plan: the subscription. The branches below distinguish states
    // that only mean something in a trial world — in particular `issue`, which
    // is "your trial activation failed, retry it". `plan.isTrial` is sticky
    // (the backend never clears it when a trial ends), so without this any
    // account that ever trialled would land on that retry modal instead of the
    // paywall, forever.
    if (!FREE_TRIAL_ENABLED) {
        return isFree ? STORE_AUTH_PAYMENT_POPUP.plan : STORE_AUTH_PAYMENT_POPUP.product;
    }

    if (isFree && !isTrial) {
        return STORE_AUTH_PAYMENT_POPUP.trial;
    }
    if (isFree && isTrial && isCancel) {
        return STORE_AUTH_PAYMENT_POPUP.plan;
    }
    if (isFree && isTrial && !isCancel) {
        return STORE_AUTH_PAYMENT_POPUP.issue;
    }
    if (!isFree && total === used) {
        return STORE_AUTH_PAYMENT_POPUP.product;
    }
    return STORE_AUTH_PAYMENT_POPUP.product;
};

// HORO-10509: maps a thrown axios error into the shape every email-auth
// thunk's rejectWithValue uses — `data` carries the backend's structured
// error payload (isEmailAlreadyExists, isEmailUnverified, retryAfter, ...)
// so components can branch on it instead of string-matching `message`.
const mapAuthError = (error) => ({
    message: error?.response?.data?.error || error.message,
    status: error?.response?.status,
    data: error?.response?.data?.data || null
});

// HORO-10509: shared by emailVerify.fulfilled and emailSignIn.fulfilled —
// both return the same `{ tokens, user, isNewUser }` shape (see the new
// /auth/verify-email and /auth/signin routes), unlike the OAuth path where
// the JWT arrives separately via the redirect's query string and this
// thunk's payload is just the /user GET response. Mirrors (a subset of)
// authUser.fulfilled's user-shape mapping below — kept separate rather than
// unified with it to avoid touching that already-working OAuth path.
const applyAuthenticatedUser = (store, { tokens, user, isNewUser }, { isSignUp }) => {
    storageService.add(STORAGE_KEY_ACCESS_TOKEN, tokens?.access);
    WSService.init();
    webSocketConnector.init(tokens?.access);

    gaEvents.setUserState('registered');
    gaEvents.setUserTier(user?.plan);
    isSignUp ? gaEvents.signUp(user?.id) : gaEvents.login(user?.id);
    // HORO-10509: auth funnel outcome — distinct from the macro signUp/login
    // events above, tracked for per-step drop-off analysis.
    isSignUp ? gaEvents.authAccountCreated() : gaEvents.authLoginSuccess();

    // HORO-10509: SecondAuthStep (birth details) is temporarily disabled —
    // there's no step 2 to route incomplete-data users into anymore, so
    // just close the modal either way. Restore the
    // `user?.dataStatus === FilledDataType.COMPLETE ? 1 : 2` / conditional
    // showAuthModal gating here when that step comes back.
    store.loading = false;
    store.accessToken = tokens?.access;
    store.registerStepCount = 1;
    store.showAuthModal = false;
    store.pendingEmail = '';
    store.authFlow = 'entry';
    // HORO-10509: "Welcome back!" toast on the sign-in path (Figma
    // 4510:29992), "Account created successfully" on the signup/verify
    // path instead (Figma 4564:52812 desktop / 4510:29794 mobile) — a
    // fresh signup isn't a returning user, so it gets its own message
    // rather than reusing signInSuccess.
    store.authMessage = isSignUp ? 'accountCreated' : 'signInSuccess';
    // HORO-10504 bugfix: this path (email/OTP) has no redirect and no `jwt`
    // URL param for useAuth's existing effect to key off — flag it here so
    // that dedicated effect runs the anonymous-data migration bundle anyway.
    store.pendingAnonymousMigration = true;
    store.user = {
        ...store.user,
        id: user?.id,
        name: user?.name,
        avatar: user?.avatar,
        email: user?.email,
        birthDate: user?.birthDate,
        birthAddress: user?.birthAddress,
        // HORO-10581: the signs are computed server-side from the birth data
        // and read straight off the user — the client no longer derives its
        // own, so they have to survive hydration or the profile header has
        // nothing to render.
        zodiacSign: user?.zodiacSign ?? null,
        moonSign: user?.moonSign ?? null,
        risingSign: user?.risingSign ?? null,
        birthDetails: user?.birthDetails ?? null,
        dataStatus: user?.dataStatus,
        phone: user?.phone,
        authStatus: user?.authStatus,
        unreadMessage: user?.unreadMessage,
        plan: { ...user?.plan },
        limits: { ...user?.limits },
        paymentPopup: setPaymentPopupState(user?.plan, user?.limits),
        defaultAdvisorAlias: user?.defaultAdvisorAlias || store.user.defaultAdvisorAlias,
        seenAdvisorIntros: user?.seenAdvisorIntros || [],
        features: user?.features || {},
        hasBirthChartReport: !!user?.hasBirthChartReport
    };
};

const initialState = {
    showAuthModal: false,
    showAnonModal: false,
    isAuthModalCanBeClosed: true,
    authModalContext: null,
    // HORO-10509: which sub-view of the (still step-1) auth modal is showing —
    // 'entry' | 'set-password' | 'verify-email' | 'sign-in' | 'forgot-password'
    // | 'reset-password' | 'add-email' | 'verify-added-email'. Kept separate
    // from registerStepCount, which still only distinguishes step 1 (identity)
    // from step 2 (birth details) exactly as it did pre-HORO-10509.
    authFlow: 'entry',
    pendingEmail: '',
    // Absolute ms timestamp the current 6-digit code (or, once verified,
    // the resetToken below) expires at, or null when there isn't an
    // outstanding one — drives ResetPasswordStep's countdown. Set on
    // emailForgotPassword.fulfilled (initial send and resend both go
    // through that same thunk) *and* on emailVerifyResetCode.fulfilled,
    // which overwrites it with the (longer-lived) resetToken's own
    // expiry — see that thunk's comment for why reusing this one field
    // for both is deliberate.
    codeExpiresAt: null,
    // Opaque token from emailVerifyResetCode.fulfilled, required by
    // emailResetPassword in place of the raw code — see both thunks'
    // comments. Cleared whenever the reset flow restarts or completes.
    resetToken: null,
    authMessage: null,
    user: {
        id: '',
        dataStatus: FilledDataType.INCOMPLETE,
        isRegister: false,
        name: '',
        avatar: '',
        birthDate: '',
        // `birthAddress` is the real field; a stray `birthPlace` key sat here
        // for years with nothing reading or writing it (HORO-10581).
        birthAddress: null,
        zodiacSign: null,
        moonSign: null,
        risingSign: null,
        birthDetails: null,
        chatIds: [],
        email: '',
        phone: '',
        authStatus: '',
        unreadMessage: 0,
        plan: {
            isAutoRenewal: false,
            isFree: true,
            isTrial: false,
            isCancel: false,
            id: '',
            validUntil: ''
        },
        paymentPopup: 'trial',
        limits: {
            planTotal: 0,
            total: 20,
            used: 0
        },
        // Multi-advisor handoff fields. Backed by user document; populated only
        // when the server returns them (i.e. when FEATURE_FLAG_MULTI_ADVISOR is on).
        // Safe to default for legacy users.
        defaultAdvisorAlias: DEFAULT_ADVISOR_ALIAS,
        seenAdvisorIntros: [],
        features: {},
        hasBirthChartReport: false,
        liveReadings: { promoDismissedAt: null, joinedWaitlistAt: null }
    },
    accessToken: '',
    refreshToken: '',
    loading: false,
    error: null,
    registerStepCount: 1,
    // HORO-10504 bugfix: set by applyAuthenticatedUser (email/OTP sign-up
    // and sign-in) so useAuth's dedicated effect knows to run the same
    // anonymous-data migration bundle (chat claim, usage/tarot carryover,
    // birth-date migration) that the OAuth redirect flow already runs via
    // its own `jwt`-gated effect — those two flows never share a code path
    // otherwise, and this one used to only fire for OAuth.
    pendingAnonymousMigration: false,
    location: {
        loading: false,
        list: [],
        error: null
    }
};

export const fetchUser = createAsyncThunk('auth/fetchUser', async (params, { rejectWithValue }) => {
    try {
        const data = await AuthApi.getUser();
        return data.data.user;
    } catch (error) {
        return rejectWithValue({ message: error.message, status: error.response.status });
    }
});

export const authUser = createAsyncThunk('auth/authUser', async (params, { rejectWithValue }) => {
    try {
        const data = await AuthApi.getUser(params);
        return data.data.user;
    } catch (error) {
        return rejectWithValue({ message: error.message, status: error.response.status });
    }
});

export const updateUser = createAsyncThunk('auth/updateUser', async (params, { rejectWithValue }) => {
    try {
        const data = await AuthApi.updateUser(params);
        return {
            user: data.data.user,
            claimedChats: data.data.claimedChats || [],
            skippedChats: data.data.skippedChats || []
        };
    } catch (error) {
        return rejectWithValue(error?.message);
    }
});

// Mark an advisor's first-time intro as seen (e.g. after the user dismisses the
// AdvisorIntroOverlay). Wraps updateUser — backend persists onto user document.
export const markAdvisorIntroSeen = createAsyncThunk('auth/markAdvisorIntroSeen', async (advisorAlias, { getState, rejectWithValue }) => {
    try {
        const state = getState();
        const current = state.auth.user.seenAdvisorIntros || [];
        if (current.includes(advisorAlias)) {
            return { user: state.auth.user, claimedChats: [], skippedChats: [] };
        }
        const next = [...current, advisorAlias];
        const data = await AuthApi.updateUser({ seenAdvisorIntros: next });
        return {
            user: data.data.user,
            claimedChats: data.data.claimedChats || [],
            skippedChats: data.data.skippedChats || []
        };
    } catch (error) {
        return rejectWithValue(error?.message);
    }
});

// Set the user's default advisor (toggled from the AdvisorIntroOverlay or settings).
// Wraps updateUser. The default determines who answers initial messages on a new chat.
export const setDefaultAdvisor = createAsyncThunk('auth/setDefaultAdvisor', async (advisorAlias, { rejectWithValue }) => {
    try {
        const data = await AuthApi.updateUser({ defaultAdvisorAlias: advisorAlias });
        return {
            user: data.data.user,
            claimedChats: data.data.claimedChats || [],
            skippedChats: data.data.skippedChats || []
        };
    } catch (error) {
        return rejectWithValue(error?.message);
    }
});

export const deleteUser = createAsyncThunk('auth/deleteUser', async (params, { rejectWithValue }) => {
    try {
        const data = await AuthApi.deleteUser();
        return data.data.user;
    } catch (error) {
        return rejectWithValue(error?.message);
    }
});

export const fetchLocations = createAsyncThunk('auth/fetchLocations', async (search, { rejectWithValue }) => {
    try {
        const data = await AuthApi.getUserLocation(search);
        return data.data;
    } catch (error) {
        return rejectWithValue(error?.message);
    }
});

export const logOut = createAsyncThunk('auth/logout', async (forceLogOut, { rejectWithValue }) => {
    try {
        if (!forceLogOut) {
            await AuthApi.userLogout();
        }
        return {
            forceLogOut
        };
    } catch (error) {
        return rejectWithValue(error?.message);
    }
});

// HORO-10509: email/password signup, signin, and password reset thunks.
// `registrationSource` (HORO-10360): what prompted the sign-up, from the auth-modal context.
export const emailSignUp = createAsyncThunk('auth/emailSignUp', async ({ email, registrationSource }, { rejectWithValue }) => {
    try {
        await AuthApi.emailSignUp(email, registrationSource);
        return { email };
    } catch (error) {
        return rejectWithValue(mapAuthError(error));
    }
});

export const emailSetPassword = createAsyncThunk(
    'auth/emailSetPassword',
    async ({ email, password, confirmPassword }, { rejectWithValue }) => {
        try {
            await AuthApi.emailSetPassword(email, password, confirmPassword);
            return { email };
        } catch (error) {
            return rejectWithValue(mapAuthError(error));
        }
    }
);

export const emailVerify = createAsyncThunk('auth/emailVerify', async ({ email, code }, { rejectWithValue }) => {
    try {
        const { data } = await AuthApi.emailVerify(email, code);
        return data.data;
    } catch (error) {
        return rejectWithValue(mapAuthError(error));
    }
});

export const emailResendCode = createAsyncThunk('auth/emailResendCode', async ({ email, purpose }, { rejectWithValue }) => {
    try {
        await AuthApi.emailResendCode(email, purpose);
        return { email, purpose };
    } catch (error) {
        return rejectWithValue(mapAuthError(error));
    }
});

export const emailSignIn = createAsyncThunk('auth/emailSignIn', async ({ email, password }, { dispatch, rejectWithValue }) => {
    try {
        const { data } = await AuthApi.emailSignIn(email, password);
        return data.data;
    } catch (error) {
        const mapped = mapAuthError(error);
        // HORO-10685: the account exists but its email was never confirmed
        // (signup abandoned before the code step). Send a fresh code so the
        // verify-email step the rejected case switches to has one to enter.
        if (mapped.data?.isEmailUnverified) {
            dispatch(emailResendCode({ email, purpose: 'signup' }));
        }
        return rejectWithValue(mapped);
    }
});

export const emailForgotPassword = createAsyncThunk('auth/emailForgotPassword', async (email, { rejectWithValue }) => {
    try {
        // Confirmed against the actual backend (media-ai-veda-backend
        // src/routes/client/auth/index.ts): whether or not an account
        // exists for this email, the response is always `{ success: true,
        // expiresInSec }` — no code, no token, ever (deliberate user-
        // enumeration protection: `expiresInSec` is a fixed constant, not
        // account-specific, so including it doesn't leak anything). The
        // 6-digit code itself is emailed as a plain OTP the user types in
        // on the next screen; there's no link/token flow to skip it with.
        // `expiresInSec` drives ResetPasswordStep's countdown — reading it
        // from the response instead of hardcoding it here means the two
        // sides can't drift out of sync the way they did before (see this
        // thunk's git history: a prior version assumed a `code` came back
        // in this same response and silently forwarded it — it never did,
        // which is what broke the live reset-password call).
        const { data } = await AuthApi.emailForgotPassword(email);
        return { email, expiresInSec: data?.expiresInSec };
    } catch (error) {
        return rejectWithValue(mapAuthError(error));
    }
});

// HORO-10509: checks the 6-digit PASSWORD_RESET code on its own, ahead of
// (and independently of) emailResetPassword below — see the backend's
// EmailAuthService.verifyResetCode. This is what actually validates the
// code the user typed on ResetPasswordCodeStep; a wrong/expired one
// rejects here, before ResetPasswordStep ever advances to the password
// screen, rather than only surfacing once a password's been submitted
// too. Fulfilled sets `resetToken`/`codeExpiresAt` (see this slice's
// state comments) for ResetPasswordNewPasswordStep to submit next.
export const emailVerifyResetCode = createAsyncThunk('auth/emailVerifyResetCode', async ({ email, code }, { rejectWithValue }) => {
    try {
        const { data } = await AuthApi.verifyResetCode(email, code);
        return { email, resetToken: data?.resetToken, expiresInSec: data?.expiresInSec };
    } catch (error) {
        return rejectWithValue(mapAuthError(error));
    }
});

export const emailResetPassword = createAsyncThunk(
    'auth/emailResetPassword',
    async ({ email, resetToken, password }, { rejectWithValue }) => {
        try {
            // `confirmPassword` sent as `password` again rather than
            // threading the user's actual confirm-password field through —
            // ResetPasswordNewPasswordStep's own client-side validation
            // already guarantees the two match before this ever dispatches,
            // so there's nothing the backend's equality check on
            // confirmPassword needs to see that `password` doesn't already
            // say twice over.
            //
            // Response is `{ success: true }` only — no tokens/user, unlike
            // emailSignIn/emailVerify. A prior version of this thunk assumed
            // an emailSignIn-shaped payload and auto-signed the user in;
            // that doesn't match what this endpoint actually returns, so
            // the fulfilled case below routes to Sign in instead (see its
            // comment).
            await AuthApi.emailResetPassword(email, resetToken, password, password);
            return { email };
        } catch (error) {
            return rejectWithValue(mapAuthError(error));
        }
    }
);

// HORO-10509: bridges an OAuth (mainly Facebook) user with no email on file
// into a real, verified one. Requires an existing session (Bearer token).
export const addEmail = createAsyncThunk('auth/addEmail', async (email, { rejectWithValue }) => {
    try {
        await AuthApi.addEmail(email);
        return { email };
    } catch (error) {
        return rejectWithValue(mapAuthError(error));
    }
});

export const verifyAddedEmail = createAsyncThunk('auth/verifyAddedEmail', async (code, { rejectWithValue }) => {
    try {
        const { data } = await AuthApi.verifyAddedEmail(code);
        return data.data;
    } catch (error) {
        return rejectWithValue(mapAuthError(error));
    }
});

export const getAuthModalState = (state) => state.auth.showAuthModal;
export const getAuthFlow = (state) => state.auth.authFlow;
export const getPendingEmail = (state) => state.auth.pendingEmail;
export const getCodeExpiresAt = (state) => state.auth.codeExpiresAt;
export const getResetToken = (state) => state.auth.resetToken;
export const getAuthMessage = (state) => state.auth.authMessage;
export const getShowAnonModal = (state) => state.auth.showAnonModal;
export const getAuthModalClosingPossibility = (state) => state.auth.isAuthModalCanBeClosed;
export const getAuthModalContext = (state) => state.auth.authModalContext;
export const getUser = (state) => state.auth.user;
export const getToken = (state) => state.accessToken;
export const getUserId = (state) => state.auth.user.id;
export const getUserUnreadMessages = (state) => state.auth.user.unreadMessage;
export const getUserDataStatus = (state) => state.auth.user.dataStatus;
export const getUserPlan = (state) => state.auth.user.plan;
export const getUserLimits = (state) => state.auth.user.limits;
export const getUserPaymentPopupState = (state) => state.auth.user.paymentPopup;

export const getRegisterStepCount = (state) => state.auth.registerStepCount;

export const getPendingAnonymousMigration = (state) => state.auth.pendingAnonymousMigration;

export const getAuthError = (state) => state.auth.error;
export const getUserLoadingState = (state) => state.auth.loading;
export const getBirthLocation = (state) => state.auth.location;
export const getUserAccessToken = (state) => state.auth.accessToken;

// Multi-advisor selectors — read additive fields. Safe before backend rollout
// (defaults provided in initialState).
export const getUserFeatures = (state) => state.auth.user.features || {};
export const getDefaultAdvisorAlias = (state) => state.auth.user.defaultAdvisorAlias || DEFAULT_ADVISOR_ALIAS;
export const getSeenAdvisorIntros = (state) => state.auth.user.seenAdvisorIntros || [];
// Backend-supplied, most-recent-first distinct advisor aliases the user has
// chatted with (cap 6). Empty array for new / anonymous users — the sidebar
// pads with the default list.
export const getRecentAdvisorAliases = (state) => state.auth.user.recentAdvisorAliases || [];
// HORO-10327: authoritative tarot pool for logged-in users.
// Anon users read straight from localStorage in useTarotCredits.
export const getUserDailyTarot = (state) => state.auth.user.dailyTarot || null;
// HORO-10599: Zara's one-time, non-resetting 3-message trial cap for
// registered-free accounts — see useZaraTrial.
export const getUserZaraTrial = (state) => state.auth.user.zaraTrial || null;

// HORO-10471: Live Readings waitlist state for the signed-in user. Anonymous
// visitors have no server-side record — see getIsOnWaitlist in waitlistSlice.
export const getUserLiveReadings = (state) => state.auth.user.liveReadings || {};
// HORO-10360: drives the My Account row state and the Entry-1 nudge.
export const getHasBirthChartReport = (state) => !!state.auth.user.hasBirthChartReport;
export const getHasJoinedWaitlist = (state) => !!state.auth.user.liveReadings?.joinedWaitlistAt;

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        toggleAuthModal(state, { payload }) {
            const { trigger, step = 1, context = null, flow, autoOpen = false } = payload;
            state.showAuthModal = trigger;
            state.registerStepCount = step;
            state.authModalContext = trigger ? context : null;
            // HORO-10509: the existing 'emailLogin' context (chat-history
            // "sign in to view" prompt) used to just swap copy on the OAuth
            // buttons — now that real email/password sign-in exists, it opens
            // straight into that form instead. Any other trigger still opens
            // on the OAuth/email-entry screen unless it explicitly asks
            // otherwise via `flow`.
            const defaultFlow = context === 'emailLogin' ? 'sign-in' : 'entry';
            state.authFlow = trigger ? flow || defaultFlow : 'entry';
            // HORO-10509: auth funnel entry points. 'add-email' (adding an
            // email to an existing OAuth-only account) is a distinct feature,
            // not a login/register entry — deliberately excluded here.
            // An app-initiated open (ProtectedRoute) isn't a click: its own event.
            if (trigger && autoOpen) {
                gaEvents.authModalAutoOpen(context);
            } else if (trigger && state.authFlow === 'sign-in') {
                gaEvents.authLoginButtonClick();
            } else if (trigger && state.authFlow === 'entry') {
                gaEvents.authRegisterClick();
            }
            if (!trigger) {
                state.pendingEmail = '';
                state.codeExpiresAt = null;
                state.resetToken = null;
                state.authMessage = null;
            }
        },
        setAuthFlow(state, { payload }) {
            state.authFlow = payload;
            state.error = null;
        },
        clearAuthMessage(state) {
            state.authMessage = null;
        },
        // HORO-10504 bugfix: dispatched by useAuth once it's run the
        // anonymous-data migration bundle for a `pendingAnonymousMigration`
        // flag set by applyAuthenticatedUser — prevents re-running it.
        clearPendingAnonymousMigration(state) {
            state.pendingAnonymousMigration = false;
        },
        setShowAnonModal(state, { payload }) {
            state.showAnonModal = payload;
        },
        setAuthModalCanBeClosed(state, { payload }) {
            state.isAuthModalCanBeClosed = payload;
        },
        setUserStatusType(state, { payload }) {
            state.user.dataStatus = payload;
        },
        setAuthToken(state, { payload }) {
            state.accessToken = payload;
        },
        setAuthRegisterStepCount(state, { payload }) {
            state.registerStepCount = payload;
        },
        clearAuthError(store) {
            store.error = null;
        },
        reduceUserUnreadMessages(store, { payload }) {
            store.user.unreadMessage = store.user.unreadMessage - payload < 0 ? 0 : store.user.unreadMessage - payload;
        },
        setUserMessagesLimits(state, { payload }) {
            state.user.limits = { ...payload };
        },
        // HORO-10327: explicit setter so the carry-over response and the
        // post-send response payload (when product wires it later) can
        // refresh the pool without a full /user refetch.
        setUserDailyTarot(state, { payload }) {
            state.user.dailyTarot = payload ? { ...payload } : null;
        },
        // HORO-10599: explicit setter so the chat-send response's echoed
        // `zaraTrial` can refresh the counter without a full /user refetch —
        // mirrors setUserDailyTarot above.
        setUserZaraTrial(state, { payload }) {
            state.user.zaraTrial = payload ? { ...payload } : null;
        },
        // HORO-10471: the waitlist join / promo-dismiss responses echo back the
        // updated `liveReadings` block, so we can reflect it without a full
        // /user refetch. Keeps the joined state and the modal suppression
        // correct for the rest of the session.
        // HORO-10360: flipped once a chart is generated so the sidebar, nudge and
        // My Account row react without a /user refetch.
        setHasBirthChartReport(state, { payload }) {
            state.user.hasBirthChartReport = !!payload;
        },
        setUserLiveReadings(state, { payload }) {
            state.user.liveReadings = {
                promoDismissedAt: payload?.promoDismissedAt || null,
                joinedWaitlistAt: payload?.joinedWaitlistAt || null
            };
        }
    },
    extraReducers(builder) {
        builder
            .addCase(fetchUser.pending, (store) => {
                store.loading = true;
            })
            .addCase(fetchUser.fulfilled, (store, { payload }) => {
                store.loading = false;
                store.accessToken = payload?.accessToken;
                store.refreshToken = payload?.refreshToken;

                WSService.init();
                webSocketConnector.init(storageService.get(STORAGE_KEY_ACCESS_TOKEN));

                store.user = {
                    ...store.user,
                    id: payload.id,
                    name: payload.name,
                    avatar: payload.avatar,
                    birthDate: payload.birthDate,
                    chatIds: payload.chatIds,
                    email: payload?.email,
                    birthAddress: payload?.birthAddress,
                    // HORO-10581: the signs are computed server-side; the client
                    // no longer derives its own, so every hydration path has to
                    // carry them or the profile header renders its empty state
                    // for a user whose chart is sitting in the database.
                    zodiacSign: payload?.zodiacSign ?? null,
                    moonSign: payload?.moonSign ?? null,
                    risingSign: payload?.risingSign ?? null,
                    birthChart: payload?.birthChart ?? null,
                    // HORO-10360: the birth date/time resolved at the birthplace; the Birth Chart
                    // flow pre-fills from it instead of re-reading `birthDate` in the browser's zone.
                    birthDetails: payload?.birthDetails ?? null,
                    dataStatus: payload.dataStatus,
                    phone: payload?.phone,
                    authStatus: payload?.authStatus,
                    unreadMessage: payload?.unreadMessage,
                    plan: {
                        ...payload?.plan
                    },
                    limits: {
                        ...payload?.limits
                    },
                    paymentPopup: setPaymentPopupState(payload?.plan, payload?.limits),
                    isMemoryRestricted: payload?.isMemoryRestricted,
                    defaultAdvisorAlias: payload?.defaultAdvisorAlias || store.user.defaultAdvisorAlias,
                    seenAdvisorIntros: payload?.seenAdvisorIntros || [],
                    features: payload?.features || {},
                    hasBirthChartReport: !!payload?.hasBirthChartReport,
                    recentAdvisorAliases: payload?.recentAdvisorAliases || store.user.recentAdvisorAliases || [],
                    dailyTarot: payload?.dailyTarot || store.user.dailyTarot || null,
                    zaraTrial: payload?.zaraTrial || store.user.zaraTrial || null,
                    // HORO-10471: this reducer enumerates fields rather than
                    // spreading the payload, so anything omitted here silently
                    // reverts to initial state on every session restore. Without
                    // it the promo modal's suppression flags read as null and it
                    // re-fires for users who already dismissed or joined.
                    liveReadings: payload?.liveReadings || store.user.liveReadings
                };

                // HORO-10426: fetchUser is the session-restore/bootstrap path
                // (returning user with a stored token, no fresh login JWT).
                // authUser.fulfilled only runs on active OAuth login, so without
                // this a returning logged-in user's events stayed tagged
                // anonymous/anonymous. Flip the analytics super-properties here
                // too so user_state / user_tier reflect the restored session.
                gaEvents.setUserState('registered');
                gaEvents.setUserTier(payload?.plan);
            })
            .addCase(fetchUser.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
            })
            .addCase(authUser.pending, (store) => {
                store.loading = true;
            })
            .addCase(authUser.fulfilled, (store, { payload }) => {
                const isSignUp = payload?.dateUpdated === payload?.dateCreated;
                // HORO-10370: flip the analytics user_state super-property so
                // all subsequent events tag as registered.
                gaEvents.setUserState('registered');
                // HORO-10426: also set the plan tier (free/trial/paid) so events
                // can be split by plan, not just anon/registered.
                gaEvents.setUserTier(payload?.plan);
                isSignUp ? gaEvents.signUp(payload?.id) : gaEvents.login(payload?.id);
                if (isSignUp && storageService.get(STORAGE_KEY_WIDGET_HOST)) {
                    gaEvents.accountCreated(payload?.id);
                }
                // HORO-10509: SecondAuthStep (birth details) is temporarily
                // disabled — see AuthModal.js. No step 2 to route
                // incomplete-data users into anymore, so just close the
                // modal either way. Restore
                // `payload?.dataStatus === FilledDataType.COMPLETE ? 1 : 2`
                // here when that step comes back.
                store.loading = false;
                store.registerStepCount = 1;
                store.showAuthModal = false;
                store.isRegister = payload.isRegister;
                store.user = {
                    ...store.user,
                    id: payload.id,
                    dataStatus: payload.dataStatus,
                    name: payload.name,
                    avatar: payload.avatar,
                    chatIds: payload.chatIds,
                    email: payload?.email,
                    birthDate: payload?.birthDate,
                    birthAddress: payload?.birthAddress,
                    // HORO-10581: the signs are computed server-side; the client
                    // no longer derives its own, so every hydration path has to
                    // carry them or the profile header renders its empty state
                    // for a user whose chart is sitting in the database.
                    zodiacSign: payload?.zodiacSign ?? null,
                    moonSign: payload?.moonSign ?? null,
                    risingSign: payload?.risingSign ?? null,
                    birthChart: payload?.birthChart ?? null,
                    // HORO-10360: the birth date/time resolved at the birthplace; the Birth Chart
                    // flow pre-fills from it instead of re-reading `birthDate` in the browser's zone.
                    birthDetails: payload?.birthDetails ?? null,
                    phone: payload?.phone,
                    isRegister: payload?.isRegister,
                    authStatus: payload?.authStatus,
                    dateUpdated: payload?.dateUpdated,
                    unreadMessage: payload?.unreadMessage,
                    plan: {
                        ...payload?.plan
                    },
                    limits: {
                        ...payload?.limits
                    },
                    paymentPopup: setPaymentPopupState(payload?.plan, payload?.limits),
                    isMemoryRestricted: payload?.isMemoryRestricted,
                    defaultAdvisorAlias: payload?.defaultAdvisorAlias || store.user.defaultAdvisorAlias,
                    seenAdvisorIntros: payload?.seenAdvisorIntros || [],
                    features: payload?.features || {},
                    hasBirthChartReport: !!payload?.hasBirthChartReport,
                    recentAdvisorAliases: payload?.recentAdvisorAliases || store.user.recentAdvisorAliases || [],
                    dailyTarot: payload?.dailyTarot || store.user.dailyTarot || null,
                    zaraTrial: payload?.zaraTrial || store.user.zaraTrial || null,
                    // HORO-10471 — see the note in fetchUser.fulfilled.
                    liveReadings: payload?.liveReadings || store.user.liveReadings
                };
            })
            .addCase(authUser.rejected, (store, { payload }) => {
                // HORO-10370: correct the optimistic pre-seed in initSession()
                // if the stored JWT turned out to be revoked / invalid on the
                // server. Without this, a user with a revoked token would keep
                // user_state='registered' for the rest of the session.
                gaEvents.setUserState('anonymous');
                gaEvents.setUserTier(null);
                store.loading = false;
                store.error = payload;
                store.showAuthModal = true;
            })
            .addCase(updateUser.pending, (store) => {
                store.loading = true;
            })
            .addCase(updateUser.fulfilled, (store, { payload }) => {
                const user = payload?.user || payload;
                const authCompleted = user.dataStatus === FilledDataType.COMPLETE;
                store.loading = false;
                store.registerStepCount = authCompleted ? 1 : 2;
                store.user = {
                    ...store.user,
                    dataStatus: user?.dataStatus,
                    name: user.name,
                    birthDate: user.birthDate,
                    chatIds: user.chatIds,
                    email: user?.email,
                    birthAddress: user?.birthAddress,
                    // HORO-10581: the signs are computed server-side; the client
                    // no longer derives its own, so every hydration path has to
                    // carry them or the profile header renders its empty state
                    // for a user whose chart is sitting in the database.
                    zodiacSign: user?.zodiacSign ?? null,
                    moonSign: user?.moonSign ?? null,
                    risingSign: user?.risingSign ?? null,
                    birthChart: user?.birthChart ?? null,
                    birthDetails: user?.birthDetails ?? null,
                    phone: user?.phone,
                    dateUpdated: user?.dateUpdated,
                    unreadMessage: user?.unreadMessage,
                    // Preserve advisor preferences across updateUser calls so that
                    // unrelated profile updates (name, birthDate, etc.) don't clobber
                    // them. When the response carries these fields, accept them.
                    defaultAdvisorAlias: user?.defaultAdvisorAlias || store.user.defaultAdvisorAlias,
                    seenAdvisorIntros: user?.seenAdvisorIntros || store.user.seenAdvisorIntros,
                    features: user?.features || store.user.features,
                    hasBirthChartReport: user?.hasBirthChartReport ?? store.user.hasBirthChartReport,
                    // HORO-10471 — same preservation reasoning as the advisor
                    // fields above: an unrelated profile update must not wipe the
                    // promo-modal suppression and make it re-fire.
                    liveReadings: user?.liveReadings || store.user.liveReadings
                };
            })
            .addCase(updateUser.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
            })
            .addCase(markAdvisorIntroSeen.fulfilled, (store, { payload }) => {
                const user = payload?.user || payload;
                if (user?.seenAdvisorIntros) {
                    store.user.seenAdvisorIntros = user.seenAdvisorIntros;
                }
            })
            .addCase(setDefaultAdvisor.fulfilled, (store, { payload }) => {
                const user = payload?.user || payload;
                if (user?.defaultAdvisorAlias) {
                    store.user.defaultAdvisorAlias = user.defaultAdvisorAlias;
                }
            })
            .addCase(deleteUser.pending, (store) => {
                store.loading = true;
                store.error = null;
            })
            .addCase(deleteUser.fulfilled, (store, { payload }) => {
                gaEvents.delete(payload?.id);
                storageService.clear();
                // HORO-10541: navigation moved out to the callers. A reducer
                // reloading the page is a side effect in the wrong place, and
                // more practically it made a post-deletion confirmation screen
                // impossible — the reload fired before anything could render.
                //
                // `return initialState` rather than the previous
                // `store = initialState`, which assigned to the local parameter
                // and so did nothing at all. The full reload hid that; without
                // it the user would stay authenticated in Redux while their
                // account no longer existed.
                return initialState;
            })
            .addCase(deleteUser.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
            })
            .addCase(fetchLocations.pending, (store) => {
                store.location.loading = true;
                store.location.error = null;
            })
            .addCase(fetchLocations.fulfilled, (store, { payload }) => {
                store.location.loading = false;
                store.location.list = payload.data;
            })
            .addCase(fetchLocations.rejected, (store, { payload }) => {
                store.location.loading = false;
                store.location.error = payload;
            })
            // HORO-10509: email signup — success just moves the sub-view
            // forward and stashes the email for the following steps; no
            // account is usable yet (backend authStatus stays PENDING) until
            // emailVerify succeeds below.
            .addCase(emailSignUp.pending, (store) => {
                store.loading = true;
                store.error = null;
                gaEvents.authRegisterEmailSubmit();
            })
            .addCase(emailSignUp.fulfilled, (store, { payload }) => {
                store.loading = false;
                store.pendingEmail = payload.email;
                store.authFlow = 'set-password';
            })
            .addCase(emailSignUp.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
                gaEvents.authRegisterEmailFailure(payload?.message, payload?.status);
            })
            .addCase(emailSetPassword.pending, (store) => {
                store.loading = true;
                store.error = null;
                gaEvents.authSetPasswordAttempt();
            })
            .addCase(emailSetPassword.fulfilled, (store, { payload }) => {
                store.loading = false;
                store.pendingEmail = payload.email;
                store.authFlow = 'verify-email';
                // HORO-10509: this is the initial verification-code send —
                // see emailResendCode.fulfilled below for the resend case.
                gaEvents.authCodeEmailSent('signup', false);
            })
            .addCase(emailSetPassword.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
                gaEvents.authSetPasswordFailure(payload?.message, payload?.status);
            })
            .addCase(emailVerify.pending, (store) => {
                store.loading = true;
                store.error = null;
                gaEvents.authVerifyCodeAttempt();
            })
            .addCase(emailVerify.fulfilled, (store, { payload }) => {
                applyAuthenticatedUser(store, payload, { isSignUp: true });
            })
            .addCase(emailVerify.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
                gaEvents.authVerifyCodeFailure(payload?.message, payload?.status);
            })
            .addCase(emailResendCode.pending, (store) => {
                store.error = null;
            })
            .addCase(emailResendCode.fulfilled, (store, { meta }) => {
                // HORO-10509: no state changes on resend success — this case
                // exists purely to fire the resend funnel event.
                gaEvents.authCodeEmailSent(meta?.arg?.purpose, true);
            })
            .addCase(emailResendCode.rejected, (store, { payload }) => {
                store.error = payload;
            })
            .addCase(emailSignIn.pending, (store) => {
                store.loading = true;
                store.error = null;
                gaEvents.authLoginAttempt();
            })
            .addCase(emailSignIn.fulfilled, (store, { payload }) => {
                applyAuthenticatedUser(store, payload, { isSignUp: false });
            })
            .addCase(emailSignIn.rejected, (store, { payload, meta }) => {
                store.loading = false;
                if (payload?.data?.isEmailUnverified) {
                    // HORO-10685: not a failed login — continue into email
                    // verification instead of showing an error on sign-in.
                    store.error = null;
                    store.pendingEmail = meta.arg.email;
                    store.authFlow = 'verify-email';
                    return;
                }
                store.error = payload;
                gaEvents.authLoginFailure(payload?.message, payload?.status);
            })
            .addCase(emailForgotPassword.pending, (store) => {
                store.loading = true;
                store.error = null;
            })
            .addCase(emailForgotPassword.fulfilled, (store, { payload }) => {
                store.loading = false;
                store.pendingEmail = payload.email;
                store.authFlow = 'reset-password';
                // Absolute timestamp, not the raw seconds-remaining — this
                // survives a re-render/remount of ResetPasswordStep without
                // needing to know when its own countdown last ticked. Also
                // set on resend (which dispatches this same thunk), so the
                // countdown restarts for free on every fresh code.
                store.codeExpiresAt = payload.expiresInSec ? Date.now() + payload.expiresInSec * 1000 : null;
                // A fresh code invalidates any resetToken from a previously
                // verified one (its raw code is already spent server-side —
                // see EmailAuthService.verifyResetCode) — clearing it here
                // covers both the initial send and every resend.
                store.resetToken = null;
            })
            .addCase(emailForgotPassword.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
            })
            .addCase(emailVerifyResetCode.pending, (store) => {
                store.loading = true;
                store.error = null;
            })
            .addCase(emailVerifyResetCode.fulfilled, (store, { payload }) => {
                store.loading = false;
                store.resetToken = payload.resetToken;
                // Overwrites the code's own countdown with the (longer-
                // lived) resetToken's — see this field's own comment for
                // why the same timestamp drives both.
                store.codeExpiresAt = payload.expiresInSec ? Date.now() + payload.expiresInSec * 1000 : null;
            })
            .addCase(emailVerifyResetCode.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
            })
            .addCase(emailResetPassword.pending, (store) => {
                store.loading = true;
                store.error = null;
            })
            .addCase(emailResetPassword.fulfilled, (store, { payload }) => {
                // No tokens/user in this response (see the thunk's
                // comment) — nothing to auto-authenticate with, so this
                // routes back to Sign in with the new password, same as
                // before this flow was redesigned.
                store.loading = false;
                store.pendingEmail = payload.email;
                store.codeExpiresAt = null;
                store.resetToken = null;
                store.authFlow = 'sign-in';
                store.authMessage = 'passwordResetSuccess';
            })
            .addCase(emailResetPassword.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
            })
            .addCase(addEmail.pending, (store) => {
                store.loading = true;
                store.error = null;
            })
            .addCase(addEmail.fulfilled, (store, { payload }) => {
                store.loading = false;
                store.pendingEmail = payload.email;
                store.authFlow = 'verify-added-email';
            })
            .addCase(addEmail.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
            })
            .addCase(verifyAddedEmail.pending, (store) => {
                store.loading = true;
                store.error = null;
            })
            .addCase(verifyAddedEmail.fulfilled, (store, { payload }) => {
                const { user } = payload;
                // HORO-10509: SecondAuthStep (birth details) is temporarily
                // disabled — see AuthModal.js. No step 2 to route
                // incomplete-data users into anymore, so just close the
                // modal either way. Restore
                // `user?.dataStatus === FilledDataType.COMPLETE ? 1 : 2` /
                // conditional showAuthModal here when that step comes back.
                store.loading = false;
                store.pendingEmail = '';
                store.user.email = user?.email;
                store.registerStepCount = 1;
                store.authFlow = 'entry';
                store.showAuthModal = false;
            })
            .addCase(verifyAddedEmail.rejected, (store, { payload }) => {
                store.loading = false;
                store.error = payload;
            })
            .addMatcher(isAnyOf(logOut.fulfilled, logOut.rejected), (store, { payload }) => {
                gaEvents.logout(store.user.id);
                // HORO-10370: flip user_state back to anonymous on logout so
                // post-logout events aren't mis-tagged as registered.
                gaEvents.setUserState('anonymous');
                gaEvents.setUserTier(null);
                // These three are per-device rate limits, not account state, so
                // they survive the wipe. Dropping them made sign-in-then-out a
                // free refill of the anonymous pools — the one thing the limits
                // exist to prevent. The message tally travels with the expiry
                // that bounds it; separating them would leave a tally with no
                // window attached, and the pool would re-arm for a full period
                // from the moment of the next read.
                const chatMessagesCounts = storageService.get(STORAGE_KEY_CHATS_MESSAGES_COUNT);
                // Carried too, for a visitor who logs out before anything has
                // rolled their pool and migrated the tally onto the current key.
                // Without this the wipe would drop it and the rename would hand
                // them a fresh allowance — the migration exists to prevent that.
                const chatMessagesCountsLegacy = storageService.get(STORAGE_KEY_CHATS_MESSAGES_COUNT_LEGACY);
                const anonMessagesExp = storageService.get(STORAGE_KEY_ANON_MESSAGES_EXP);
                const dailyTarotCredits = storageService.get(STORAGE_KEY_DAILY_TAROT);
                const chatSavedMessages = storageService.get(STORAGE_KEY_SAVED_USER_MESSAGES);
                const memoryAnnouncementKey = `${STORAGE_KEY_MEMORY_ANNOUNCEMENT_SEEN}_${store.user.id}`;
                const memoryAnnouncementSeen = storageService.get(memoryAnnouncementKey);

                if (payload?.forceLogOut) {
                    store.user = {
                        ...initialState.user
                    };
                    store.showAuthModal = true;
                    store.accessToken = '';
                    store.refreshToken = '';
                    store.loading = false;
                    store.error = null;
                    store.registerStepCount = 1;
                } else {
                    store = initialState;
                }

                storageService.clear();
                storageService.add(STORAGE_KEY_CHATS_MESSAGES_COUNT, chatMessagesCounts);
                storageService.add(STORAGE_KEY_ANON_MESSAGES_EXP, anonMessagesExp);
                storageService.add(STORAGE_KEY_CHATS_MESSAGES_COUNT_LEGACY, chatMessagesCountsLegacy);
                storageService.add(STORAGE_KEY_DAILY_TAROT, dailyTarotCredits);
                storageService.add(STORAGE_KEY_SAVED_USER_MESSAGES, chatSavedMessages);
                if (memoryAnnouncementSeen) {
                    storageService.add(memoryAnnouncementKey, memoryAnnouncementSeen);
                }
                window.location.replace(getAppBasename());
            });
    }
});

export const authReducers = authSlice.reducer;
export const {
    toggleAuthModal,
    setShowAnonModal,
    setUserStatusType,
    setAuthToken,
    setAuthRegisterStepCount,
    clearAuthError,
    reduceUserUnreadMessages,
    setUserMessagesLimits,
    setUserDailyTarot,
    setUserZaraTrial,
    setUserLiveReadings,
    setHasBirthChartReport,
    setAuthModalCanBeClosed,
    setAuthFlow,
    clearAuthMessage,
    clearPendingAnonymousMigration
} = authSlice.actions;
